import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import config from '../config';
import { Duration } from '../constants';
import { useAxios } from '../context/AxiosContextProvider';

export interface Analytics {
  id: number;
  entry_key: string;
  value: number;
  country: string;
  date: string;
}

async function getAnalytics(props: {
  axios: AxiosInstance;
  start_date: string;
  end_date: string;
  unit?: string;
  country?: string;
  kpis?: string;
}): Promise<Analytics> {
  const { axios, start_date, end_date, unit, country, kpis } = props;
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/controlling`, {
    params: {
      start_date,
      end_date,
      unit,
      country,
      kpis,
    },
  });
  return data;
}

export default function useAnalyticsQuery(start: Date, end: Date, unit?: string, kpis?: string, queryKey?: string, country?: string) {
  const axios = useAxios();
  // convert date to the following format: yyyy-mm-dd. Has nothing to do with Sweden
  const start_date = start.toLocaleDateString('sv-SE');
  const end_date = end.toLocaleDateString('sv-SE');
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey(start_date, end_date, unit, country, kpis),
    queryFn: () => getAnalytics({ axios, start_date, end_date, unit, country, kpis }),
    staleTime: Duration.Minute * 5,
    enabled: !!start_date && !!end_date,
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, JSON.stringify(query.error as any));
    }
  }, [query.error, query.isError, toast]);

  return query;
}

function getKey(start_date: string, end_date: string, unit?: string, country?: string, kpis?: string) {
  return ['Analytics', start_date, end_date, unit, country, kpis] as const satisfies QueryKey;
}
useAnalyticsQuery.getKey = getKey;
